import React from 'react';

import { Button, Input, RemoveIcon, Select, Textarea, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { limitLength, validateNumberAndHyphenOnly } from '~/utils';

import {
  RTS_DIRECT_BUSINESS_CODE_MAX_LENGTH,
  RTS_DIRECT_CONTACT_NAME_MAX_LENGTH,
  RTS_DIRECT_CONTACT_PHONE_MAX_LENGTH,
  RTS_DIRECT_RELEASE_ADDRESS_MAX_LENGTH,
} from '~/constants';

import type { TypeRtsDirectRentalCard as TypeRtsDirectRentalCardData } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalRtsDirectModal.module.scss';

type TypeRtsDirectRentalCardProps = {
  card: TypeRtsDirectRentalCardData;
  rentalCompanyOptions: TypeSelectOption<string>[];
  /** 카드가 2개일 때만 [렌탈사 삭제] 노출 (최소 1개 유지) */
  showRemove: boolean;
  onChange: (id: string, patch: Partial<TypeRtsDirectRentalCardData>) => void;
  /** 담당자 연락처 포커스 이탈 — 부모가 그 카드의 인라인 에러를 다시 판정한다 */
  onBlur: (id: string) => void;
  onRemove: (id: string) => void;
};

/**
 * [입고관리 > 회송/폐기 관리] 회수(직배) 신청 모달 - 렌탈사 입력 그룹
 *
 * @note 렌탈사/업체코드/담당자 성함·연락처/반출 상세 주소를 입력하며 전부 필수. 2개 이상일 때 [렌탈사 삭제] 노출.
 *       업체코드·성함은 20자, 연락처는 숫자·하이픈 15자까지만 받고 글자수를 표기한다. 연락처 미입력·형식 오류는 포커스 이탈 시 인라인으로 알린다.
 */
const RtsDirectRentalCard = ({
  card,
  rentalCompanyOptions,
  showRemove,
  onChange,
  onBlur,
  onRemove,
}: TypeRtsDirectRentalCardProps) => {
  return (
    <div className={styles['rental-group']}>
      <FormField
        label="렌탈사"
        required
        contents={
          <Select
            size="small"
            placeholder="렌탈사 선택"
            maxWidth="229px"
            dropdownPortalTarget={document.body}
            shouldCloseOnNotInView
            value={card.rentalCompany}
            options={rentalCompanyOptions}
            onChange={(_, option: TypeSelectOption<string> | null) =>
              onChange(card.id, { rentalCompany: option ?? null })
            }
          />
        }
      />
      <FormField
        label="업체코드"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="업체코드 입력"
            maxWidth="229px"
            maxLength={RTS_DIRECT_BUSINESS_CODE_MAX_LENGTH}
            count
            value={card.businessCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(card.id, { businessCode: limitLength(e.target.value, RTS_DIRECT_BUSINESS_CODE_MAX_LENGTH) })
            }
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(card.id, { businessCode: e.target.value.trim() })
            }
          />
        }
      />
      <FormField
        label="담당자 성함"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="성함 입력"
            maxWidth="229px"
            maxLength={RTS_DIRECT_CONTACT_NAME_MAX_LENGTH}
            count
            value={card.contactName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(card.id, { contactName: limitLength(e.target.value, RTS_DIRECT_CONTACT_NAME_MAX_LENGTH) })
            }
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(card.id, { contactName: e.target.value.trim() })
            }
          />
        }
      />
      <FormField
        label="담당자 연락처"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="예: 010-1234-5678"
            maxWidth="229px"
            className={styles['contact-phone-input']}
            maxLength={RTS_DIRECT_CONTACT_PHONE_MAX_LENGTH}
            count
            status={card.contactPhoneError ? 'error' : 'default'}
            validationMessage={card.contactPhoneError}
            value={card.contactPhone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              // 숫자·하이픈 외 문자는 입력 자체를 받지 않는다
              if (validateNumberAndHyphenOnly(e.target.value)) {
                onChange(card.id, { contactPhone: e.target.value });
              }
            }}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(card.id, { contactPhone: e.target.value.trim() });
              onBlur(card.id);
            }}
          />
        }
      />
      <FormField
        label="반출 상세 주소"
        required
        contentAlign="start"
        labelClassName={styles['textarea-label']}
        contents={
          <Textarea
            size="small"
            maxWidth="503px"
            height="78px"
            placeholder="예: 경기도 김포시 고촌읍 아라육로 75, A동 3층"
            count
            maxLength={RTS_DIRECT_RELEASE_ADDRESS_MAX_LENGTH}
            value={card.releaseAddress}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChange(card.id, { releaseAddress: e.target.value })
            }
          />
        }
      />
      {showRemove && (
        <div className={styles['rental-action']}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            leftIcon={<RemoveIcon />}
            onClick={() => onRemove(card.id)}
          >
            렌탈사 삭제
          </Button>
        </div>
      )}
    </div>
  );
};

export default RtsDirectRentalCard;
